import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DomEvent } from 'leaflet';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import MapOverlay from './MapOverlay';
import MapOverlayList from './MapOverlayList';
import { EMapType } from '../../../zustand/useMap';
import MapMarkers from './MapMarkers';
import MapFitBounds from './MapFitBound';
import MapEventCoordinate from './MapEventCoordinate';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import MapControl from './MapControl';
import { LastDataLogProps } from '../HomeCustom';
import MapSettings from './MapSettings';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { APP_THEME } from '../../../const/config';
import useMasterData from '../../../hooks/useMasterData';
import MapZoom from './MapZoom';

export interface IMapConfig {
  mapType: string;
  isShowMapCoordinate: boolean;
  isShowMapAttribution: boolean;
  isShowMapControl: boolean;
  isShowMapLayers: boolean;
  isShowMapPolyline: boolean;
  isShowMapSettings: boolean;
  showVesselOnline: boolean;
  showVesselOffline: boolean;
  showVesselWaiting: boolean;
  showVesselLabel: boolean;
  showVesselDotTrail: boolean;
  trailWidth: number;
  trailOpacity: number;
  trailLength?: number;
  zoom?: number;
  showTimezoneLayer?: boolean;
  showWindLayer?: boolean;
  showWaveLayer?: boolean;
  activeLayers?: string;
}

export interface IParamsMapCustomProps {
  height?: string;
  companyId: string;
  lastDataLog?: LastDataLogProps;
  loadingData: boolean;
  mapConfig: IMapConfig;
  showAssetsIds?: string[];
  isMobile?: boolean;
}

const INITIAL_CENTER: [number, number] = [
  -5.869903075110989, 105.75813825058138,
];
const INITIAL_ZOOM = 8;

const MapCustom: React.FC<IParamsMapCustomProps> = ({
  height = '100vh',
  companyId,
  mapConfig,
  lastDataLog,
  loadingData,
  showAssetsIds,
  isMobile,
}) => {
  const minZoom = 4;
  const maxZoom = 15;
  const [zoomLevel, setZoomLevel] = useState<number>(INITIAL_ZOOM);
  const [lastMarkersBound, setLastMarkersBound] = useState<any>([]);
  const { pathname } = useLocation();
  const [showModalSettings, setShowModalSettings] = useState<boolean>(false);
  const [showModalLayers, setShowModalLayers] = useState<boolean>(false);
  const [hasFitInitialBounds, setHasFitInitialBounds] =
    useState<boolean>(false);
  const mapPopupRef = useRef<HTMLDivElement>(null);
  const showAssetsKey = useMemo(() => showAssetsIds?.join(',') || '', [
    showAssetsIds,
  ]);

  const handleOpenModalSettings = () => {
    setShowModalLayers(false);
    setShowModalSettings(!showModalSettings);
  };

  const handleOpenModalLayers = () => {
    setShowModalSettings(false);
    setShowModalLayers(!showModalLayers);
  };

  useEffect(() => {
    if (!mapPopupRef.current) {
      return;
    }

    DomEvent.disableClickPropagation(mapPopupRef.current);
    DomEvent.disableScrollPropagation(mapPopupRef.current);
  }, [showModalLayers, showModalSettings]);

  const MapEventHandler: React.FC = () => {
    const map = useMapEvents({});
    useEffect(() => {
      const t = setTimeout(() => {
        try {
          if (map && typeof map.invalidateSize === 'function') {
            map.invalidateSize();
          }
        } catch (e) {
          // ignore if map container is not available (map might be unmounted)
        }
      }, 100);
      return () => clearTimeout(t);
    }, [map]);

    return null;
  };

  useEffect(() => {
    let markers: any = [];
    if (lastDataLog && Object.keys(lastDataLog).length > 0 && !loadingData) {
      Object.keys(lastDataLog).forEach((assetName) => {
        const gps = lastDataLog[assetName].gps;
        if (gps && Object.keys(gps).length > 1) {
          const gpsFlat = Object.values(gps)[Object.keys(gps).length - 1];
          markers.push([gpsFlat.latitude, gpsFlat.longitude]);
        } else if (gps && Object.keys(gps).length === 1) {
          const gpsFlat = Object.values(gps)[0];
          markers.push([gpsFlat.latitude, gpsFlat.longitude]);
        }
      });
    }

    if (markers && markers.length) {
      setLastMarkersBound(markers);
    }
  }, [lastDataLog, loadingData]);

  const filteredLastDataLog = useMemo(() => {
    // console.log('showAssetsIds', showAssetsIds);
    // console.log('lastDataLog', lastDataLog);
    let filteredData: any = {};
    if (lastDataLog) {
      if (showAssetsIds && showAssetsIds.length > 0) {
        Object.values(lastDataLog).forEach((item) => {
          if (showAssetsIds.includes(item.assetId) && item.gps) {
            filteredData[item.name] = { ...item };
          }
        });
      } else {
        // filteredData = { ...lastDataLog };
      }
    }
    // console.log('filteredData', filteredData);
    return filteredData;
  }, [lastDataLog, showAssetsIds]);

  useEffect(() => {
    if (mapConfig.zoom) {
      setZoomLevel(mapConfig.zoom);
    }
  }, [mapConfig.zoom]);

  useEffect(() => {
    // the map fits the markers of the new company or assets once they load
    setLastMarkersBound([]);
    setHasFitInitialBounds(false);
  }, [companyId, showAssetsKey]);

  // console.log('filteredLastDataLog', filteredLastDataLog);

  return (
    <MapCustomFrame $height={height}>
      <MapContainer
        center={INITIAL_CENTER}
        zoom={INITIAL_ZOOM}
        scrollWheelZoom={true}
        zoomControl={false}
        attributionControl={false}
        style={{ height: '100%', width: '100%' }}
        maxZoom={maxZoom}
        minZoom={minZoom}
      >
        <TileLayer
          url={`${
            mapConfig.mapType === EMapType.vector_white
              ? 'https://cartodb-basemaps-d.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png'
              : mapConfig.mapType === EMapType.vector_color
                ? 'https://{s}.tile.openstreetmap.de/{z}/{x}/{y}.png'
                : 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'
          }`}
        />

        <MapOverlay isMobile={isMobile} />

        {/* fitted once per company and assets: refitting on every poll of
            the last data moves the map away from what is being drawn */}
        {lastMarkersBound &&
          lastMarkersBound.length > 0 &&
          !hasFitInitialBounds && (
            <MapFitBounds
              markers={lastMarkersBound}
              maxZoom={8}
              onFitted={() => setHasFitInitialBounds(true)}
            />
          )}

        <MapEventHandler />

        {mapConfig && mapConfig.zoom && (
          <MapCustomZoomHandler zoom={mapConfig.zoom} />
        )}

        {filteredLastDataLog && (
          <MapMarkers
            trailWidth={mapConfig.trailWidth}
            trailOpacity={mapConfig.trailOpacity}
            showVesselOffline={mapConfig.showVesselOffline}
            showVesselOnline={mapConfig.showVesselOnline}
            showVesselWaiting={mapConfig.showVesselWaiting}
            showVesselDotTrail={mapConfig.showVesselDotTrail}
            showVesselLabel={mapConfig.showVesselLabel}
            lastDataLog={filteredLastDataLog}
            zoomLevel={zoomLevel}
          />
        )}

        {mapConfig.isShowMapControl && (
          <MapControl
            handleOpenModalSettings={handleOpenModalSettings}
            handleOpenModalLayers={handleOpenModalLayers}
            showMapSettings={mapConfig.isShowMapSettings}
            showMapLayers={mapConfig.isShowMapLayers}
            showMapPolyline={mapConfig.isShowMapPolyline}
          />
        )}

        {/* Modal Map Layers */}
        {showModalLayers && (
          <ModalSettings
            ref={mapPopupRef}
            path={pathname}
            $variant='layers'
            height={['/home', '/custom-home'].includes(pathname) ? 440 : 270}
          >
            <TitleModal>Map Layers</TitleModal>
            <MapSettingsWrapper>
              <MapOverlayList compact={pathname === '/dashboard'} />
            </MapSettingsWrapper>
          </ModalSettings>
        )}

        {mapConfig.isShowMapCoordinate && (
          <MapEventCoordinate showSummary={false} />
        )}

        {mapConfig.isShowMapAttribution && (
          <AttributionWrapper path={pathname}>
            <AttributionCustom path={pathname}>{`© ${dayjs().year()} ${
              APP_THEME !== 'bki' ? APP_THEME.toUpperCase() : 'Powered by RAMUS'
            }`}</AttributionCustom>
          </AttributionWrapper>
        )}
      </MapContainer>

      {loadingData && (
        <LoadingCustom style={{ top: isMobile ? 90 : 12 }}>
          <Spin
            style={{ color: 'white', fill: 'white', stroke: 'white' }}
            indicator={
              <LoadingOutlined
                style={{
                  color: 'white',
                  borderWidth: 10,
                  borderColor: 'white',
                  fill: 'white',
                  stroke: 'white',
                }}
                spin
              />
            }
            size='small'
          />
          <div style={{ color: 'white' }}>Fetching data...</div>
        </LoadingCustom>
      )}

      {/* Modal Map Setting */}
      {showModalSettings && (
        <ModalSettings
          ref={mapPopupRef}
          path={pathname}
          $variant='settings'
          height={['/home', '/custom-home'].includes(pathname) ? 340 : 270}
        >
          <TitleModal>Map Settings</TitleModal>
          <MapSettingsWrapper>
            <MapSettings compact={pathname === '/dashboard'} />
          </MapSettingsWrapper>
        </ModalSettings>
      )}
    </MapCustomFrame>
  );
};

const MapCustomZoomHandler: React.FC<{ zoom: number }> = ({ zoom }) => {
  const map = useMap();

  useEffect(() => {
    map.setZoom(zoom);
  }, [map, zoom]);

  return null;
};

const MapCustomFrame = styled.div<{ $height: string }>`
  position: relative;
  width: 100%;
  height: ${({ $height }) => $height};
  min-width: 0;
  overflow: hidden;

  @media (max-width: 767px) {
    .leaflet-top.leaflet-right {
      top: 10px;
      right: 10px;
    }

    .leaflet-top.leaflet-right .leaflet-control {
      margin-top: 0;
      margin-right: 0;
    }
  }
`;

const LoadingCustom = styled.div`
  padding: 6px 12px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  color: white;
  z-index: 998;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  top: 12px;
  left: 50%;
  transform: translate(-50%, 0);
  font-size: 12px;
`;

const AttributionCustom = styled.div<{ path: string }>`
  padding: 4px 16px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  color: white;
  z-index: 998;
  bottom: ${(props) =>
    ['/home', '/custom-home'].includes(props.path) ? '70px' : '10px'};
  right: 12px;
`;

const AttributionWrapper = styled.div<{ path: string }>`
  z-index: 99;
  /* .leaflet-container .leaflet-control-attribution {
    bottom: ${({ path }) =>
    ['/home', '/custom-home'].includes(path) ? '10px' : '0px'} !important;
  } */
`;

type ModalSettingsStyleProps = {
  height?: number;
  path?: string;
  $variant: 'layers' | 'settings';
};

const ModalSettings = styled.div<ModalSettingsStyleProps>`
  width: ${(params) =>
    params.path && ['/home', '/custom-home'].includes(params.path)
      ? '320px'
      : params.path && ['/dashboard'].includes(params.path)
        ? '520px'
        : '640px'};
  border-radius: 8px;
  position: absolute;
  z-index: 990;
  bottom: ${(params) =>
    params.path && ['/home', '/custom-home'].includes(params.path)
      ? '120px'
      : '55px'};
  right: 60px;
  max-height: 820px;
  overflow-y: auto;
  background: white;
  height: ${({ height }) => {
    return height ? height + 'px' : '340px';
  }};

  @media (max-width: 767px) {
    width: min(260px, calc(100vw - 76px));
    max-width: 260px;
    max-height: calc(100dvh - 176px);
    height: ${({ $variant }) =>
      $variant === 'layers'
        ? 'min(300px, calc(100dvh - 176px))'
        : 'min(280px, calc(100dvh - 176px))'};
    display: grid;
    grid-template-rows: 40px minmax(0, 1fr);
    left: auto;
    right: 52px;
    overflow: hidden;
    overscroll-behavior: contain;
    touch-action: pan-y;
  }

  @media (max-width: 767px) {
    ${({ path, $variant }) =>
      path && ['/dashboard'].includes(path)
        ? `
            width: min(240px, calc(100vw - 76px));
            max-width: 240px;
            height: ${
              $variant === 'layers'
                ? 'min(240px, calc(100dvh - 176px))'
                : 'min(220px, calc(100dvh - 176px))'
            };
          `
        : ''}
  }
`;

const TitleModal = styled.div`
  background-color: #e8ecf3;
  padding: 0 15px;
  height: 50px;
  color: #3f4c5f;
  font-weight: 600;
  display: flex;
  font-size: 16px;
  align-items: center;

  @media (max-width: 767px) {
    height: 40px;
    padding: 0 12px;
    font-size: 14px;
  }
`;

const MapSettingsWrapper = styled.div`
  padding: 0 15px;

  @media (max-width: 767px) {
    min-height: 0;
    padding: 0 8px;
    overflow-y: auto;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
  }
`;

export default MapCustom;
